import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * SafeMarkdown — render assistant prose with a tight, calm component set.
 *
 * Never produces raw `**`, `##`, `---` tokens; never injects HTML; styles are
 * already calm and consistent with the design tokens. Use for AI text where
 * Claude may have emitted soft markdown.
 */
const COMPONENTS = {
    h1: ({ children }) => (
        <div className="text-display-sm mt-4 mb-2">{children}</div>
    ),
    h2: ({ children }) => (
        <div className="text-display-sm mt-4 mb-2">{children}</div>
    ),
    h3: ({ children }) => (
        <div className="text-[15px] font-medium text-stone-900 mt-4 mb-2">
            {children}
        </div>
    ),
    // Render the "paragraph" component as <div> so block-level children
    // (fenced code, hr, lists) never nest inside <p> and trigger React's
    // "<p> cannot contain a nested <pre>" hydration warning.
    p: ({ children }) => (
        <div className="text-body text-stone-700 leading-relaxed mb-3 last:mb-0">
            {children}
        </div>
    ),
    ul: ({ children }) => (
        <ul className="list-disc pl-5 space-y-1.5 my-3 text-stone-700 text-body">
            {children}
        </ul>
    ),
    ol: ({ children }) => (
        <ol className="list-decimal pl-5 space-y-1.5 my-3 text-stone-700 text-body">
            {children}
        </ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => (
        <strong className="font-medium text-stone-900">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    hr: () => <div className="my-4 border-t border-stone-100" />,
    code: ({ inline, children }) =>
        inline ? (
            <code className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-800 font-mono text-[13px]">
                {children}
            </code>
        ) : (
            <pre className="p-3 bg-stone-50 rounded-md font-mono text-[13px] text-stone-800 overflow-x-auto">
                <code>{children}</code>
            </pre>
        ),
    a: ({ href, children }) => (
        <a
            href={href}
            className="text-[var(--brand)] hover:underline underline-offset-2"
            target="_blank"
            rel="noreferrer"
        >
            {children}
        </a>
    ),
    blockquote: ({ children }) => (
        <blockquote className="pl-4 border-l-2 border-stone-200 text-stone-600 my-3">
            {children}
        </blockquote>
    ),
};

export function SafeMarkdown({ children, className = "" }) {
    if (!children) return null;
    return (
        <div className={className}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={COMPONENTS}
                skipHtml
            >
                {String(children)}
            </ReactMarkdown>
        </div>
    );
}
